import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWorkloadStatistics,
  getUnassignedAffaires,
  autoAssignAffaire,
  processUnassignedAffaires,
  redistributeFromUser,
  rebalanceAllAffaires,
  type WorkloadStatistics,
  type UnassignedAffaire
} from '@/features/admin/tabs/tracker/api/workload.api';

/**
 * Hook to fetch workload statistics for all responsables
 */
export function useWorkloadStatistics() {
  return useQuery<WorkloadStatistics, Error>({
    queryKey: ['workload-statistics'],
    queryFn: getWorkloadStatistics,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000 // Consider data stale after 10 seconds
  });
}

/**
 * Hook to fetch unassigned affaires
 */
export function useUnassignedAffaires() {
  return useQuery<UnassignedAffaire[], Error>({
    queryKey: ['unassigned-affaires'],
    queryFn: getUnassignedAffaires,
    refetchInterval: 60000 // Refetch every minute
  });
}

/**
 * Hook to manually assign a single affaire
 */
export function useAutoAssignAffaire() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: autoAssignAffaire,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['workload-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-affaires'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-affaire-settings'] });
    }
  });
}

/**
 * Hook to process all unassigned affaires (bulk assignment)
 */
export function useProcessUnassignedAffaires() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processUnassignedAffaires,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['workload-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-affaires'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-affaire-settings'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-channels'] });
    }
  });
}

/**
 * Hook to redistribute affaires from a specific user
 */
export function useRedistributeFromUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: redistributeFromUser,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['workload-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['task-responsibilities'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-affaire-settings'] });
    }
  });
}

/**
 * Hook to rebalance all affaires based on current capacities
 */
export function useRebalanceAllAffaires() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rebalanceAllAffaires,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['workload-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['task-responsibilities'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-affaire-settings'] });
    }
  });
}
